import { Modal, Radio, useToastMessage } from 'ccs-ui-components';
import _ from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import useLoader from '../../../common/hooks/Loader';
import { getEventSchema } from '../../store/_api';
import { PayloadTransformation } from '../../modules/journey-canvas/utility/TypeDefs';
import { CheckBoxTree } from './checkbox-tree/CheckBoxTree';
import { CheckBoxTreeData, IPayloadTransformationProps } from './utilities/TypeDefs';
import { extractPayloadFields } from './utilities/Utils';

import classes from './PayloadTransformationModal.module.scss';

export const PayloadTransformationModal = ({ services, eventName, eventVersion, onClose, show, payloadTransformations, onSave, nodeId, readOnly }: IPayloadTransformationProps) => {
  const loader = useLoader();
  const toastMessage = useToastMessage();
  const { t: translate } = useTranslation();

  const [selectedService, setSelectedService] = useState('');
  const [payloadOptions, setPayloadOptions] = useState<CheckBoxTreeData[]>([]);
  const [duplicates, setDuplicates] = useState<string[]>([]);
  const [selectedPayloadMap, setSelectedPayloadMap] = useState<{ [key: string]: boolean }>({});

  const localPayloadTransformsRef = useRef<PayloadTransformation[]>([]);
  const arrayFieldsRef = useRef<string[]>([]);
  const selectedPayloads = useMemo(() => Object.keys(selectedPayloadMap).filter((k) => selectedPayloadMap[k]), [selectedPayloadMap]);

  const saveTransformPayloadModal = () => {
    onSave(localPayloadTransformsRef.current, nodeId);
  };

  const selectServiceHandler = useCallback((selectedService: string, selectedPayloadMap: { [key: string]: boolean }) => {
    setSelectedService(selectedService);

    const service = localPayloadTransformsRef.current.find((pt) => pt.ingress === selectedService && pt.egress === nodeId);

    Object.keys(selectedPayloadMap).forEach((k) => { selectedPayloadMap[k] = false; });
    if (service) {
      service.persistentAttributes.forEach((v) => { selectedPayloadMap[v] = true; });
    }
    setSelectedPayloadMap({ ...selectedPayloadMap });
  }, [nodeId]);

  const checkChangeHandler = (value: string, checked: boolean) => {
    selectedPayloadMap[value] = checked;
    const nodeIdParts = value.split('.');
    // change value of all children
    Object.keys(selectedPayloadMap).filter((k) => k.startsWith(`${value}.`)).forEach((k) => { selectedPayloadMap[k] = checked; });
    // keep checking all parents
    while (nodeIdParts.length > 0) {
      const targetNodeVal = selectedPayloadMap[nodeIdParts.join('.')];
      const parent = nodeIdParts.slice(0, nodeIdParts.length - 1).join('.');
      if (targetNodeVal) {
        // all siblings has same value?
        const allSiblingSameValue = Object.keys(selectedPayloadMap).filter((k) => k.startsWith(`${parent}.`)).every((k) => selectedPayloadMap[k] === checked);
        if (allSiblingSameValue) {
          selectedPayloadMap[parent] = targetNodeVal;
        } else {
          selectedPayloadMap[parent] = false;
        }
      } else {
        // all parents will be unchecked
        selectedPayloadMap[parent] = false;
      }
      nodeIdParts.pop();
    }

    const allPayloadKeys = Object.keys(selectedPayloadMap);
    const selectedLeafNodes = allPayloadKeys.filter((k) => selectedPayloadMap[k]).filter((k) => !allPayloadKeys.find((kk) => kk.startsWith(`${k}.`))).filter((v) => !!v);

    const existingPayloadIndex = localPayloadTransformsRef.current.findIndex(
      (pt) => pt.ingress === selectedService && pt.egress === nodeId
    );

    if (existingPayloadIndex !== -1) {
      const existingPayload = localPayloadTransformsRef.current[existingPayloadIndex];

      if (selectedLeafNodes.length > 0) {
        existingPayload.persistentAttributes = selectedLeafNodes;
      } else {
        localPayloadTransformsRef.current.splice(existingPayloadIndex, 1);
      }
    } else {
      localPayloadTransformsRef.current.push({
        egress: nodeId,
        ingress: selectedService,
        persistentAttributes: selectedLeafNodes,
      });
    }

    const allSelectedPayloads: string[] = localPayloadTransformsRef.current
      .map((pt) => pt.persistentAttributes)
      .flat();

    const isArrayField = (field: string) =>
      arrayFieldsRef.current.some((af) => field === af || field.startsWith(`${af}.`));

    const duplicates: string[] = allSelectedPayloads
      .filter((pa, index, self) => self.indexOf(pa) !== index)
      .filter((pa) => !isArrayField(pa));

    setDuplicates(duplicates);

    setSelectedPayloadMap({ ...selectedPayloadMap });
  };

  useEffect(() => {
    if (show) {
      localPayloadTransformsRef.current = _.cloneDeep(payloadTransformations);
      arrayFieldsRef.current = [];
      setDuplicates([]);

      loader.show();
      (async () => {
        const selectedPayloadMap: { [key: string]: boolean } = {};
        const eventSchemaResponse = await getEventSchema(eventName, eventVersion);
        if (eventSchemaResponse) {
          const parsedPayloadData = JSON.parse(eventSchemaResponse.schema);
          const flattenedFields: string[] = [];
          let fieldToParse = parsedPayloadData.fields[0].type;
          if (Array.isArray(fieldToParse)) {
            fieldToParse = fieldToParse[1].fields;
          } else {
            fieldToParse = fieldToParse.fields;
          }
          const nestedPayloadData = extractPayloadFields(fieldToParse, flattenedFields, [], arrayFieldsRef.current);
          setPayloadOptions(nestedPayloadData);
          flattenedFields.forEach((f) => { selectedPayloadMap[f] = false; });
          setSelectedPayloadMap(selectedPayloadMap);
        } else {
          toastMessage.failure(translate('journey_builder_component.title_toast_error'), translate('journey_builder_component.msg_toast_body_error'));
        }
        loader.hide();

        selectServiceHandler(services[0]?.value ?? '', selectedPayloadMap);
      })();
    }
  }, [show, eventName, services, selectServiceHandler, payloadTransformations]);

  return (
    <Modal
      modalTitle={translate('payload_transformation_component.lbl_title')}
      modalBody={
        <div className={classes['payload-modal']} data-testid="transform-payload-modal">
          {toastMessage.renderToast()}
          {loader.render()}
          <div className={classes['payload-transform-data-container']}>
            <div className={classes['payload-service']}>
              <div className={classes['payload-service-text']}>
                <span className={classes.mandatory}>*</span>
                <div>{translate('payload_transformation_component.lbl_services')}</div>
              </div>
              {services.map((item) => (
                <div className={classes['payload-service-data']} key={item.value}>
                  <Radio
                    name={item.name}
                    value={item.value}
                    label={item.name}
                    checked={selectedService === item.value}
                    onChange={() => selectServiceHandler(item.value, selectedPayloadMap)}
                    data-testid="payload-service"
                  />
                </div>
              ))}
            </div>
            <div className={classes['choose-payload']}>
              <div className={classes['choose-payload-text']}>
                <span className={classes.mandatory}>*</span>
                <div>{translate('payload_transformation_component.lbl_choose_payload')}</div>
              </div>
              <div className={classes['choose-payload-data']}>
                <CheckBoxTree
                  nodes={payloadOptions}
                  checkedNodes={selectedPayloads}
                  onCheckChange={checkChangeHandler}
                  duplicates={duplicates}
                  disabledCheckBox={readOnly}
                />
              </div>
            </div>
          </div>
        </div>
      }
      modalShow={show}
      btnSaveText={readOnly ? undefined : translate('payload_transformation_component.lbl_save_btn') || ''}
      btnCloseText={translate('payload_transformation_component.lbl_cancel_btn') || ''}
      disableCloseBtn={false}
      onSaveModal={saveTransformPayloadModal}
      onCloseModal={onClose}
      alignToCentre
      className="modal-xl"
      disableSaveBtn={duplicates.length > 0}
    />
  );
};
